import { writable, derived, get } from 'svelte/store'
import type { DocumentInfo } from '$lib/storage/types'
import { documents } from './fileStore'
import { storage } from '$lib/storage/StorageManager'
import { contentIndexStore } from './contentIndexStore'
import type { SearchResult } from '$lib/types/contentIndex'

export interface FileSearchMatch {
  lineNumber: number
  lineText: string
  startIndex: number
  endIndex: number
  context: string
}

export interface FileSearchResult {
  fileId: string
  fileName: string
  matches: FileSearchMatch[]
  isExpanded: boolean
  lastModified?: Date
  createdAt?: Date
}

export interface FileSearchState {
  searchTerm: string
  results: FileSearchResult[]
  isSearching: boolean
}

// No longer needed - ContentIndex handles document indexing internally

function createFileSearchStore() {
  const initialState: FileSearchState = {
    searchTerm: '',
    results: [],
    isSearching: false
  }
  
  const { subscribe, set, update } = writable<FileSearchState>(initialState)
  
  // ContentIndex handles all caching and indexing internally
  
  // Check if ContentIndex is initialized
  async function ensureContentIndexReady() {
    const state = contentIndexStore.getState()
    
    // Wait a bit if currently indexing (initial load case)
    if (state.isIndexing && !state.isInitialized) {
      // Wait up to 5 seconds for initialization to complete
      for (let i = 0; i < 50; i++) {
        await new Promise(resolve => setTimeout(resolve, 100))
        const newState = contentIndexStore.getState()
        if (newState.isInitialized) {
          return true
        }
        if (!newState.isIndexing) {
          break // Indexing finished but still not initialized
        }
      }
    }
    
    if (!state.isInitialized) {
      // ContentIndex should be initialized by vaultStore
      return false
    }
    return true
  }
  
  // ContentIndex handles all indexing automatically
  
  // Optimized search function using ContentIndex
  async function search(term: string, matchCase: boolean = false) {
    
    if (!term) {
      update(state => ({
        ...state,
        searchTerm: '',
        results: [],
        isSearching: false
      }))
      return
    }
    
    update(state => ({
      ...state,
      searchTerm: term,
      isSearching: true
    }))
    
    // Ensure ContentIndex is ready
    const contentIndexReady = await ensureContentIndexReady()
    
    if (!contentIndexReady) {
      update(state => ({
        ...state,
        isSearching: false,
        results: []
      }))
      return
    }
    
    try {
      // Use ContentIndex for search
      const searchResults: SearchResult[] = await contentIndexStore.search(term, {
        fuzzy: true,
        limit: 500,
        includeContext: true
      })
      
      // Convert ContentIndex results to FileSearchResult format
      const resultsMap = new Map<string, FileSearchResult>()
      
      for (const result of searchResults) {
        // Get or create file result
        let fileResult = resultsMap.get(result.path)
        if (!fileResult) {
          // Extract filename from path
          const fileName = result.path.split('/').pop() || result.path
          
          fileResult = {
            fileId: result.path, // Use path as ID
            fileName: fileName.replace(/\.md$/, ''), // Remove .md extension
            matches: [],
            isExpanded: true,
            lastModified: undefined, // ContentIndex doesn't provide this yet
            createdAt: undefined
          }
          resultsMap.set(result.path, fileResult)
        }
        
        // Process content to find actual line matches
        if (result.content) {
          const lines = result.content.split('\n')
          const searchTermProcessed = matchCase ? term : term.toLowerCase()
          
          // Skip YAML frontmatter
          let startLine = 0
          if (lines[0] === '---') {
            for (let i = 1; i < lines.length; i++) {
              if (lines[i] === '---') {
                startLine = i + 1
                break
              }
            }
          }
          
          // Find all matches in the content
          for (let i = startLine; i < lines.length; i++) {
            const line = lines[i]
            const lineProcessed = matchCase ? line : line.toLowerCase()
            
            // Find all occurrences in this line
            let position = 0
            while (position < lineProcessed.length) {
              const matchIndex = lineProcessed.indexOf(searchTermProcessed, position)
              if (matchIndex === -1) break
              
              // Get context (previous and next lines)
              const contextLines: string[] = []
              if (i > 0 && lines[i - 1].trim()) contextLines.push(lines[i - 1])
              contextLines.push(line)
              if (i < lines.length - 1 && lines[i + 1].trim()) contextLines.push(lines[i + 1])
              
              fileResult.matches.push({
                lineNumber: i + 1,
                lineText: line,
                startIndex: matchIndex,
                endIndex: matchIndex + term.length,
                context: contextLines.join('\n')
              })
              
              position = matchIndex + 1 // Move past this match
            }
          }
        } else if (result.matches && result.matches.length > 0) {
          // Fallback: use provided matches if no content
          for (const match of result.matches) {
            const lineNumber = match.position?.line || 1
            const lineText = match.text
            const context = match.context || match.text
            
            const searchTermLower = term.toLowerCase()
            const textLower = lineText.toLowerCase()
            const startIndex = textLower.indexOf(searchTermLower)
            const endIndex = startIndex !== -1 ? startIndex + term.length : lineText.length
            
            fileResult.matches.push({
              lineNumber: lineNumber,
              lineText: lineText,
              startIndex: startIndex !== -1 ? startIndex : 0,
              endIndex: endIndex,
              context: context
            })
          }
        } else if (result.preview) {
          // Last fallback: use preview
          const searchTermLower = term.toLowerCase()
          const previewLower = result.preview.toLowerCase()
          const matchIndex = previewLower.indexOf(searchTermLower)
          
          if (matchIndex !== -1) {
            fileResult.matches.push({
              lineNumber: 1,
              lineText: result.preview,
              startIndex: matchIndex,
              endIndex: matchIndex + term.length,
              context: result.preview
            })
          }
        }
      }
      
      // Convert to array and sort by relevance
      const results = Array.from(resultsMap.values())
      
      // Sort results: files with more matches first
      results.sort((a, b) => b.matches.length - a.matches.length)
      
      // Sort matches within each file by line number
      for (const result of results) {
        result.matches.sort((a, b) => {
          if (a.lineNumber !== b.lineNumber) {
            return a.lineNumber - b.lineNumber
          }
          return a.startIndex - b.startIndex
        })
      }
      
      update(state => ({
        ...state,
        results,
        isSearching: false
      }))
      
    } catch (error) {
      console.error('Search failed:', error)
      update(state => ({
        ...state,
        results: [],
        isSearching: false
      }))
    }
  }
  
  // Toggle result expansion
  function toggleExpanded(fileId: string) {
    update(state => ({
      ...state,
      results: state.results.map(result =>
        result.fileId === fileId
          ? { ...result, isExpanded: !result.isExpanded }
          : result
      )
    }))
  }
  
  // Update index for a specific document (incremental update)
  async function updateFileIndex(doc: DocumentInfo) {
    // ContentIndex handles incremental updates automatically
    // through file system watcher. We just need to re-search
    // if there's an active search term.
    
    const state = get({ subscribe })
    if (state.searchTerm) {
      // Small delay to allow ContentIndex to update
      setTimeout(async () => {
        await search(state.searchTerm)
      }, 100)
    }
  }
  
  // Remove file from index
  function removeFileIndex(fileId: string) {
    // ContentIndex handles file removal automatically
    // Just remove from current search results
    update(state => ({
      ...state,
      results: state.results.filter(r => r.fileId !== fileId)
    }))
  }
  
  // Clear search
  function clear() {
    update(state => ({
      ...state,
      searchTerm: '',
      results: [],
      isSearching: false
    }))
  }
  
  // Clear cache and rebuild index
  async function clearCache() {
    // ContentIndex handles its own cache management
    await contentIndexStore.clearCache()
  }
  
  // Set searching state
  function setSearching(isSearching: boolean) {
    update(state => ({ ...state, isSearching }))
  }
  
  return {
    subscribe,
    search,
    toggleExpanded,
    updateFileIndex,
    removeFileIndex,
    clear,
    clearCache,
    setSearching
  }
}

export const fileSearchStore = createFileSearchStore()

// Derived store for search stats
export const fileSearchStats = derived(fileSearchStore, $search => {
  const totalMatches = $search.results.reduce((sum, result) => sum + result.matches.length, 0)
  const fileCount = $search.results.length
  
  return {
    totalMatches,
    fileCount,
    hasResults: totalMatches > 0
  }
})